import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Video from 'twilio-video';
import { reduxPropTypes } from '@figure/frontlib-onboarding-redux';
import { Width, Error } from '@figure/frontlib-gui';
import { TwilioVideo } from 'shared/components';

const StyledContainer = styled.div`
  position: relative;
  overflow: hidden;
`;

const StyledBody = styled.div`
  padding: 15px;

  @media print, screen and (min-width: ${Width.MD}px) {
    padding: 30px;
  }
`;

const FundingSession = ({
  notarySession,
  notaryComplete,
  handleError,
  handleCancel,
  handleAccept,
  handleSubmit,
  handleConnected,
  serverError,
}) => {
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState(0);
  const notaryParticipated = useRef(false);
  const canceled = useRef(false);
  const roomRef = useRef(null);

  useEffect(() => {
    joinRoom(notarySession.accessToken);

    window.addEventListener('beforeunload', () => onCancel());

    return () => {
      window.removeEventListener('beforeunload', () => onCancel());
      onCancel();
    };
  }, []);

  useEffect(() => {
    if (!notaryParticipated.current && participants > 1) {
      notaryParticipated.current = true;
      handleConnected();
      setTimeout(() => {
        handleAccept(); // delay event slightly to reduce race conditions
      }, 3000);
    }
  }, [participants, handleAccept, handleConnected]);

  const joinRoom = (accessToken) => {
    if (!accessToken) return;
    // TODO: handle Twilio connect errors
    Video.connect(accessToken, {
      name: 'existing-room',
      audio: true,
      video: true,
      preferredVideoCodecs: ['H264'],
      networkQuality: true,
    }).then(
      (room) => {
        setRoom(room);
        roomRef.current = room;
      },
      (e) => handleError(e)
    );
  };

  const onCancel = () => {
    roomRef.current?.disconnect();
    if (!canceled.current && !notaryParticipated.current) {
      canceled.current = true;

      handleCancel();
    }
  };

  return (
    <StyledContainer>
      <TwilioVideo
        room={room}
        continuable={notaryComplete || participants <= 1}
        onParticipantConnect={() => setParticipants((prevParticipants) => prevParticipants + 1)}
        onParticipantDisconnect={() => setParticipants((prevParticipants) => prevParticipants - 1)}
        onDisconnect={() => {
          handleSubmit();
        }}
        minimize={!notarySession.accepted}
        position={notarySession.position}
      />

      <StyledBody>
        <Error display="block">{serverError}</Error>
      </StyledBody>
    </StyledContainer>
  );
};

FundingSession.propTypes = {
  notarySession: reduxPropTypes.notarySession.isRequired,
  notaryComplete: PropTypes.bool.isRequired,
  handleAccept: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleConnected: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  serverError: PropTypes.string.isRequired,
};

export default FundingSession;
